import { useState, useRef, useEffect } from 'react';
import IL_ILCE, { ILLER } from '../data/ilIlce';

/*
  ŞEHİR + İLÇE OTOMATİK TAMAMLAMA BİLEŞENİ
  ----------------------------------------
  Kullanım:
    <SehirIlceSecici
      sehir={form.sehir}
      ilce={form.ilce}
      onSehirChange={(v) => setForm({ ...form, sehir: v, ilce: '' })}
      onIlceChange={(v) => setForm({ ...form, ilce: v })}
    />

  - "i" yazınca İstanbul, İzmir, Isparta... önerilir (Türkçe harf duyarlı)
  - Şehir seçilince ilçe kutusu o şehrin ilçelerini önerir
  - Şehir seçilmeden ilçe yazılırsa tüm ilçelerde arar ve
    seçilen ilçenin şehrini de otomatik doldurur
*/

// Türkçe duyarlı küçük harfe çevirme (I->ı, İ->i sorunu için)
function trKucult(s) {
  return (s || '').toLocaleLowerCase('tr-TR');
}

function trBaslar(metin, aranan) {
  return trKucult(metin).startsWith(trKucult(aranan));
}

function trIcerir(metin, aranan) {
  return trKucult(metin).includes(trKucult(aranan));
}

const stiller = {
  kapsayici: { position: 'relative', flex: 1, minWidth: 120 },
  liste: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
    background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 'var(--r)', maxHeight: 220, overflowY: 'auto',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
  },
  oge: {
    padding: '10px 14px', cursor: 'pointer', fontSize: 14,
    borderBottom: '1px solid var(--border)'
  },
  ogeAlt: { fontSize: 11, color: 'var(--text2)', marginLeft: 6 }
};

function OneriListesi({ oneriler, onSec, altBilgi }) {
  if (!oneriler.length) return null;
  return (
    <div style={stiller.liste}>
      {oneriler.map((o, i) => (
        <div
          key={i}
          style={stiller.oge}
          onMouseDown={(e) => { e.preventDefault(); onSec(o); }}
        >
          {typeof o === 'string' ? o : (
            <>
              {o.ilce}
              <span style={stiller.ogeAlt}>{o.il}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SehirIlceSecici({ sehir, ilce, onSehirChange, onIlceChange, stil }) {
  const [sehirAcik, setSehirAcik] = useState(false);
  const [ilceAcik, setIlceAcik] = useState(false);
  const kapsayiciRef = useRef(null);

  // Dış tıklamada listeleri kapat
  useEffect(() => {
    function disTiklama(e) {
      if (kapsayiciRef.current && !kapsayiciRef.current.contains(e.target)) {
        setSehirAcik(false);
        setIlceAcik(false);
      }
    }
    document.addEventListener('mousedown', disTiklama);
    return () => document.removeEventListener('mousedown', disTiklama);
  }, []);

  // --- Şehir önerileri: önce baş harfle eşleşenler, sonra içerenler ---
  const sehirOneri = !sehir ? [] : [
    ...ILLER.filter(il => trBaslar(il, sehir)),
    ...ILLER.filter(il => !trBaslar(il, sehir) && trIcerir(il, sehir))
  ].slice(0, 8);

  // --- İlçe önerileri ---
  let ilceOneri = [];
  if (ilce) {
    const seciliIl = ILLER.find(il => trKucult(il) === trKucult(sehir || ''));
    if (seciliIl) {
      // Şehir seçili: sadece o şehrin ilçeleri
      const liste = IL_ILCE[seciliIl] || [];
      ilceOneri = [
        ...liste.filter(i => trBaslar(i, ilce)),
        ...liste.filter(i => !trBaslar(i, ilce) && trIcerir(i, ilce))
      ].slice(0, 8);
    } else {
      // Şehir boş: tüm Türkiye ilçelerinde ara, şehriyle birlikte göster
      const hepsi = [];
      for (const [il, ilceler] of Object.entries(IL_ILCE)) {
        for (const i of ilceler) {
          if (trBaslar(i, ilce)) hepsi.push({ il, ilce: i });
        }
      }
      ilceOneri = hepsi.slice(0, 8);
    }
  }

  return (
    <div ref={kapsayiciRef} style={{ display: 'flex', gap: 8, ...stil }}>
      {/* ŞEHİR */}
      <div style={stiller.kapsayici}>
        <input
          className="input"
          placeholder="Şehir"
          value={sehir || ''}
          onChange={(e) => { onSehirChange(e.target.value); setSehirAcik(true); }}
          onFocus={() => setSehirAcik(true)}
          autoComplete="off"
        />
        {sehirAcik && (
          <OneriListesi
            oneriler={sehirOneri}
            onSec={(il) => {
              onSehirChange(il);
              if (ilce && !(IL_ILCE[il] || []).some(i => trKucult(i) === trKucult(ilce))) {
                onIlceChange('');
              }
              setSehirAcik(false);
            }}
          />
        )}
      </div>

      {/* İLÇE */}
      <div style={stiller.kapsayici}>
        <input
          className="input"
          placeholder="İlçe"
          value={ilce || ''}
          onChange={(e) => { onIlceChange(e.target.value); setIlceAcik(true); }}
          onFocus={() => setIlceAcik(true)}
          autoComplete="off"
        />
        {ilceAcik && (
          <OneriListesi
            oneriler={ilceOneri}
            onSec={(secim) => {
              if (typeof secim === 'string') {
                onIlceChange(secim);
              } else {
                onIlceChange(secim.ilce);
                onSehirChange(secim.il); // şehri de otomatik doldur
              }
              setIlceAcik(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
